import React from "react";
import { Link } from "react-router-dom";
import muhammadPic from "../../assets/images/me.png";
import { siteConfig } from "../../config/site";
import { useIsDanish } from "../../i18n/LanguageContext";
import "./About.css";

const team = [
  {
    name: "Muhammad Bangash",
    title: "CEO & Founder",
    img: muhammadPic,
    bio: "Software Engineer educated at the Technical University of Denmark (DTU), Muhammad has spent years at the intersection of software engineering and cloud architecture. Before founding System32, he built deep expertise helping enterprise and mid-market companies migrate to Azure and hybrid environments — delivering secure, compliant, and cost-efficient outcomes. He founded System32 in 2024 with a single conviction: that most companies are not failing at technology, they are failing at the space between their applications and the cloud. System32 exists to close that gap.",
    linkedin: siteConfig.linkedinUrl,
  },
  // Add more team members here
];

const teamDa = [
  {
    name: "Muhammad Bangash",
    title: "CEO & Founder",
    img: muhammadPic,
    bio: "Muhammad er software engineer uddannet fra Danmarks Tekniske Universitet (DTU) og har arbejdet i krydsfeltet mellem softwareudvikling og cloudarkitektur. Før han stiftede System32, opbyggede han erfaring med at hjælpe enterprise- og mid-market-virksomheder med Azure, hybridmiljøer, governance og sikre platforme. System32 blev stiftet i 2024 ud fra en enkel observation: mange virksomheder fejler ikke på teknologi alene, men i mellemrummet mellem applikationer, cloud, drift og governance. System32 er skabt for at lukke det mellemrum.",
    linkedin: siteConfig.linkedinUrl,
  },
];

const values = [
  { icon: "🇩🇰", label: "Danish-based", desc: `Headquartered in Denmark. CVR: ${siteConfig.cvr}.` },
  { icon: "🛡️", label: "Security-first", desc: "Every engagement is Zero Trust by default." },
  { icon: "☁️", label: "Microsoft-only", desc: "Deep specialisation, no generalist trade-offs." },
  { icon: "🤝", label: "Outcome-driven", desc: "We measure success by your results, not hours billed." },
];

const valuesDa = [
  { icon: "🇩🇰", label: "Dansk virksomhed", desc: `Hjemmehørende i Danmark. CVR: ${siteConfig.cvr}.` },
  { icon: "🛡️", label: "Sikkerhed først", desc: "Sikkerhed og adgangsstyring tænkes ind fra start." },
  { icon: "☁️", label: "Cloud og integrationer", desc: "Vi arbejder der, hvor applikationer, cloud, API'er og governance mødes." },
  { icon: "🤝", label: "Resultatdrevet", desc: "Vi måler succes på det, kunden kan drive videre med." },
];

const About = () => {
  const isDa = useIsDanish();
  const currentTeam = isDa ? teamDa : team;
  const currentValues = isDa ? valuesDa : values;

  return (
    <div className="about-page">

      {/* Hero */}
      <section className="about-hero">
        <div className="about-hero-inner">
          <span className="about-eyebrow">{isDa ? "Vores historie" : "Our story"}</span>
          <h1>{isDa ? <>Bygget i Danmark.<br />Bygget til cloud.</> : <>Built in Denmark.<br />Built for the cloud.</>}</h1>
          <p className="about-hero-sub">
            {isDa
              ? "System32 blev stiftet i 2024 for at hjælpe virksomheder med at få styr på mellemrummet mellem applikationer, cloud, integrationer og drift, og bygge løsninger der er sikre, stabile og forståelige fra starten."
              : "System32 was founded in 2024 with one purpose: to help companies understand and master the space between their applications and the cloud — and build infrastructure that is safe, secure, and reliable by design."}
          </p>
        </div>
      </section>

      {/* Company story */}
      <section className="about-story">
        <div className="about-container">
          <div className="about-story-grid">
            <div className="about-story-text">
              <h2>{isDa ? "Hvem vi er" : "Who we are"}</h2>
              <p>
                {isDa
                  ? "System32 er et dansk konsulenthus etableret i 2024. Vi arbejder med cloud governance, Azure, Entra ID, integrationer, API'er, CI/CD, automatisering og sikker softwareudvikling, især der hvor flere systemer skal fungere sammen i drift."
                  : "System32 is a Danish Microsoft-first consultancy established in 2024. We specialise in Azure governance, Entra ID, cloud migrations, CI/CD transformation, and secure software development — exclusively on the Microsoft platform."}
              </p>
              <p>
                {isDa
                  ? "Virksomheden er stiftet af Muhammad Bangash, software engineer fra Danmarks Tekniske Universitet (DTU). Han så det samme mønster igen og igen: problemer opstår ofte mellem applikationsteamet, cloudmiljøet, adgangsstyringen og de forretningssystemer, der skal hænge sammen. Det er dér sikkerhedsrisici, stigende omkostninger og fastlåste projekter opstår."
                  : "The company was founded by Muhammad Bangash, a Software Engineer from the Technical University of Denmark (DTU), who identified a recurring pattern across organisations: the gap between application teams and cloud infrastructure is where security incidents happen, costs spiral, and projects stall. System32 was built to own that gap and eliminate it."}
              </p>
              <p>
                {isDa
                  ? "Vi hjælper kunder med at flytte workloads til cloud, samle fragmenterede identitetsmiljøer, etablere governance, bygge sikre API'er og integrationer, automatisere processer og overdrage løsninger, som kundens egne teams kan drive videre."
                  : "In a short time, we have helped clients move hundreds of servers to Azure, consolidate fragmented identity systems, implement Zero Trust access controls, and ship production-grade event-driven platforms — all on Microsoft-native tooling."}
              </p>
              <Link to="/use-cases" className="about-link-cta">{isDa ? "Læs vores kundecases →" : "Read our client stories →"}</Link>
            </div>
            <div className="about-story-values">
              {currentValues.map(v => (
                <div key={v.label} className="about-value-card">
                  <span className="about-value-icon">{v.icon}</span>
                  <div>
                    <strong>{v.label}</strong>
                    <p>{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="about-team">
        <div className="about-container">
          <h2>{isDa ? "Teamet" : "The team"}</h2>
          <p className="about-team-sub">
            {isDa
              ? "Et fokuseret team af engineers og arkitekter med erfaring fra rigtige produktionsmiljøer, rigtige systemer og rigtige kunder."
              : "A focused team of engineers and architects who have done this before — in production, at scale, for real clients."}
          </p>
          <div className="about-team-grid">
            {currentTeam.map(member => (
              <div key={member.name} className="about-profile-card">
                <div className="about-profile-img-wrap">
                  <img src={member.img} alt={member.name} className="about-profile-img" />
                </div>
                <div className="about-profile-info">
                  <h3>{member.name}</h3>
                  <span className="about-profile-title">{member.title}</span>
                  <p className="about-profile-bio">{member.bio}</p>
                  {member.linkedin && (
                    <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="about-profile-linkedin">
                      <i className="fab fa-linkedin-in" /> LinkedIn
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta">
        <div className="about-container">
          <h2>{isDa ? "Klar til at lukke hullet?" : "Ready to close the gap?"}</h2>
          <p>{isDa ? "Book en uforpligtende strategisamtale, og lad os tale om jeres cloud-, software- eller integrationsudfordring." : "Book a no-obligation strategy call and let's talk about your cloud or software challenge."}</p>
          <a href="/#contact-section" className="about-cta-btn">{isDa ? "Book en strategisamtale" : "Book a strategy call"}</a>
        </div>
      </section>

    </div>
  );
};

export default About;
